<template>
  <v-container fluid class="px-0">
    <v-card elevation="2" class="mx-auto">
      <v-card-title class="white--text blue-grey darken-2 py-4 px-6 d-flex align-center">
        <v-icon left color="white">mdi-car-multiple</v-icon>
        Listagem de Veículos
        <v-spacer></v-spacer>
        <v-btn
            color="white"
            outlined
            @click="$router.push({ name: 'cadastros-veiculos-novo' })"
        >
          <v-icon>mdi-plus</v-icon>
          <span class="ml-2 d-none d-sm-flex">Novo Veículo</span>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Filtros -->
      <v-expansion-panels v-model="panelFiltros" accordion flat>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div class="d-flex align-center">
              <v-icon left color="primary">mdi-filter-variant</v-icon>
              <span class="font-weight-medium">Filtros de Busca</span>
              <v-spacer></v-spacer>
              <v-chip
                  v-if="filtrosAtivos > 0"
                  small
                  color="primary"
                  text-color="white"
                  class="mr-2"
              >
                {{ filtrosAtivos }} filtro{{ filtrosAtivos > 1 ? 's' : '' }} ativo{{ filtrosAtivos > 1 ? 's' : '' }}
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-divider/>
          <v-expansion-panel-content>
            <v-card-text class="pt-0">
              <v-row dense>
                <!-- Empresa -->
                <v-col cols="12" md="3">
                  <v-select
                      v-model="filtros.idfilial"
                      :items="filiaisFiltro"
                      item-text="filial"
                      item-value="idfilial"
                      label="Empresa"
                      outlined
                      dense
                      clearable
                      prepend-inner-icon="mdi-office-building"
                      @change="listarVeiculos(); aplicarFiltros()"
                      hide-details
                  />
                </v-col>

                <!-- Veículo -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                      v-model="filtros.idveiculo"
                      :items="veiculosFiltro"
                      item-text="veiculoplaca"
                      item-value="idveiculo"
                      label="Veículo/Placa"
                      outlined
                      dense
                      clearable
                      prepend-inner-icon="mdi-car"
                      @change="aplicarFiltros"
                      hide-details
                  />
                </v-col>

                <!-- Combustível -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                      v-model="filtros.idproduto"
                      :items="produtos"
                      item-text="produto"
                      item-value="idproduto"
                      label="Combustível"
                      outlined
                      dense
                      clearable
                      prepend-inner-icon="mdi-fuel"
                      @change="aplicarFiltros"
                      hide-details
                  />
                </v-col>

                <!-- Botão Limpar Filtros -->
                <v-col cols="12" md="3">
                  <v-btn
                      color="grey"
                      outlined
                      block
                      @click="limparFiltros"
                      height="40"
                      :disabled="filtrosAtivos === 0"
                  >
                    <v-icon left>mdi-filter-remove</v-icon>
                    Limpar Filtros
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-divider></v-divider>

      <!-- Tabela -->
      <v-card-text class="pa-0">
        <v-data-table
            :headers="headers"
            :items="veiculosFiltrados"
            :loading="carregando"
            :options.sync="options"
            :footer-props="{
            'items-per-page-text': 'Itens por página:',
            'items-per-page-options': [10, 25, 50, 100, { text: 'Todos', value: -1 }]
          }"
            class="elevation-0"
            no-data-text="Nenhum veículo encontrado"
            loading-text="Carregando veículos..."
        >
          <!-- Slot para formatação do ano -->
          <template v-slot:item.anofabricacao="{ item }">
            <span>{{ item.anofabricacao || '-' }}</span>
          </template>

          <!-- Slot para formatação do hodômetro -->
          <template v-slot:item.odometro="{ item }">
            <span v-if="item.odometro">{{ Number(item.odometro).toLocaleString('pt-BR') }} KM</span>
            <span v-else class="grey--text">-</span>
          </template>

          <!-- Slot para ações -->
          <template v-slot:item.acoes="{ item }">
            <div class="d-flex gap-1">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click="visualizarVeiculo(item)"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>Visualizar</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                      icon
                      small
                      color="orange"
                      v-bind="attrs"
                      v-on="on"
                      @click="editarVeiculo(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Editar</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                      icon
                      small
                      color="red"
                      v-bind="attrs"
                      v-on="on"
                      @click="confirmarExclusao(item)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Excluir</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog de Visualização -->
    <v-dialog v-model="dialogVisualizacao" max-width="900px" scrollable>
      <v-card v-if="veiculoSelecionado">
        <v-card-title class="blue-grey darken-2 white--text">
          <v-icon left color="white">mdi-eye</v-icon>
          Detalhes do Veículo
          <v-spacer></v-spacer>
          <v-btn icon color="white" @click="dialogVisualizacao = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" md="6">
              <div class="info-item">
                <div class="info-label">Empresa:</div>
                <div class="info-value">{{ veiculoSelecionado.empresa || '-' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <div class="info-label">Nome do Veículo:</div>
                <div class="info-value">{{ veiculoSelecionado.nomeveiculo || '-' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <div class="info-label">Marca:</div>
                <div class="info-value">{{ veiculoSelecionado.marca || '-' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <div class="info-label">Modelo:</div>
                <div class="info-value">{{ veiculoSelecionado.modelo || '-' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <div class="info-label">Placa:</div>
                <div class="info-value">{{ veiculoSelecionado.placa || '-' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <div class="info-label">Cor:</div>
                <div class="info-value">{{ veiculoSelecionado.cor || '-' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <div class="info-label">Ano de Fabricação:</div>
                <div class="info-value">{{ veiculoSelecionado.anofabricacao || '-' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <div class="info-label">Hodômetro:</div>
                <div class="info-value">
                  {{ veiculoSelecionado.odometro ? Number(veiculoSelecionado.odometro).toLocaleString('pt-BR') + ' KM' : '-' }}
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <div class="info-label">RENAVAM:</div>
                <div class="info-value">{{ veiculoSelecionado.renavam || '-' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <div class="info-label">Chassi:</div>
                <div class="info-value">{{ veiculoSelecionado.chassi || '-' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <div class="info-label">Usuário Responsável:</div>
                <div class="info-value">{{ veiculoSelecionado.usuario || '-' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="info-item">
                <div class="info-label">Combustível:</div>
                <div class="info-value">{{ veiculoSelecionado.produto || '-' }}</div>
              </div>
            </v-col>
            <v-col cols="12">
              <div class="info-item">
                <div class="info-label">Observações:</div>
                <div class="info-value">{{ veiculoSelecionado.observacao || 'Nenhuma observação' }}</div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog de Confirmação de Exclusão -->
    <v-dialog v-model="dialogExclusao" max-width="500px">
      <v-card>
        <v-card-title class="red darken-2 white--text">
          <v-icon left color="white">mdi-alert</v-icon>
          Confirmar Exclusão
        </v-card-title>

        <v-card-text class="pa-6 pb-2 text-center">
          <v-icon size="80" color="red" class="mb-4">mdi-delete-alert</v-icon>
          <div class="text-h6 mb-2">Tem certeza que deseja excluir este veículo?</div>
          <div class="grey--text">
            Veículo: <strong>{{ veiculoParaExcluir?.nomeveiculo }}</strong><br>
            Placa: <strong>{{ veiculoParaExcluir?.placa }}</strong>
          </div>
          <v-alert
              type="error"
              outlined
              class="mt-4 text-left"
          >
            Esta ação não pode ser desfeita!
          </v-alert>
        </v-card-text>
        <v-divider/>
        <v-card-actions class="px-6 pb-5 pt-4">
          <v-spacer></v-spacer>
          <v-btn
              color="grey"
              outlined
              @click="dialogExclusao = false"
              :disabled="excluindo"
          >
            Cancelar
          </v-btn>
          <v-btn
              color="red"
              class="white--text"
              @click="excluirVeiculo"
              :loading="excluindo"
              elevation="1"
              :disabled="excluindo"
          >
            <v-icon left>mdi-delete</v-icon>
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para feedback -->
    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        right
    >
      <v-icon left color="white">{{ snackbar.icon }}</v-icon>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            color="white"
            v-bind="attrs"
            @click="snackbar.show = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import axios from "axios";
import mixinFilial from "../../../util/mixinFilial";

export default {
  mixins: [mixinFilial],

  data() {
    return {
      carregando: false,
      excluindo: false,

      // Controle de painéis e dialogs
      panelFiltros: null,
      dialogVisualizacao: false,
      dialogExclusao: false,

      // Dados originais (todos os veículos)
      veiculosOriginais: [],
      produtos: [],

      // Veículo selecionado para ações
      veiculoSelecionado: null,
      veiculoParaExcluir: null,

      // Configuração da tabela
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['nomeveiculo'],
        sortDesc: [false]
      },

      // Filtros
      filtros: {
        idfilial: null,
        idveiculo: null,
        idproduto: null
      },

      // Headers da tabela
      headers: [
        { text: 'Empresa', value: 'empresa', sortable: true },
        { text: 'Veículo', value: 'nomeveiculo', sortable: true },
        { text: 'Placa', value: 'placa', width: '120px', sortable: true },
        { text: 'Ano', value: 'anofabricacao', width: '100px', sortable: true },
        { text: 'Hodômetro', value: 'odometro', width: '120px', align: 'center', sortable: true },
        { text: 'Combustível', value: 'produto', width: '130px', sortable: true },
        { text: 'Ações', value: 'acoes', align: 'center', width: '110px', sortable: false }
      ],

      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        icon: 'mdi-check-circle',
        timeout: 4000
      }
    };
  },

  computed: {
    ...mapState(["backendUrl", "timeLimit", "usuario"]),

    filiaisFiltro() {
      if (this.filiais.lista) {
        return this.filiais.lista.filter((v) => {
          return (
              v.idfilial !== 4 &&
              v.idfilial !== 6 &&
              v.idfilial !== 12 &&
              v.idfilial !== 13 &&
              v.idfilial !== 25 &&
              v.idfilial !== 29 &&
              v.idfilial !== 33 &&
              v.idfilial !== 37
          );
        });
      } else {
        return [];
      }
    },

    veiculosFiltro() {
      // Retorna todos os veículos para o autocomplete
      return this.veiculosOriginais.map(v => ({
        ...v,
        veiculoplaca: `${v.nomeveiculo} - ${v.placa}`
      }));
    },

    veiculosFiltrados() {
      let veiculos = [...this.veiculosOriginais];

      // Aplicar filtros
      if (this.filtros.idfilial) {
        veiculos = veiculos.filter(v => v.idfilial === this.filtros.idfilial);
      }

      if (this.filtros.idveiculo) {
        veiculos = veiculos.filter(v => v.idveiculo === this.filtros.idveiculo);
      }

      if (this.filtros.idproduto) {
        veiculos = veiculos.filter(v => v.idproduto === this.filtros.idproduto);
      }

      return veiculos;
    },

    filtrosAtivos() {
      let count = 0;
      if (this.filtros.idfilial) count++;
      if (this.filtros.idveiculo) count++;
      if (this.filtros.idproduto) count++;
      return count;
    }
  },

  methods: {
    async listarVeiculos(idfilial) {
      this.filtros.idveiculo = null;
      this.carregando = true;

      try {
        const response = await axios.post(`${this.backendUrl}tms/veiculo/listar`, {
          idfilial: this.filtros.idfilial || idfilial ||null
        });

        if (response.data && Array.isArray(response.data)) {
          this.veiculosOriginais = response.data;
        } else {
          this.veiculosOriginais = [];
        }

      } catch (error) {
        console.error('Erro ao carregar veículos:', error);
        this.mostrarSnackbar('Erro ao carregar veículos', 'error');
        this.veiculosOriginais = [];
      } finally {
        this.carregando = false;
      }
    },

    async carregarDados(idfilial) {
      try {
        await Promise.all([
          this.listarVeiculos(idfilial),
          this.listarProdutos()
        ]);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        this.mostrarSnackbar('Erro ao carregar dados iniciais', 'error');
      }
    },

    async listarProdutos() {
      try {
        const response = await axios.post(`${this.backendUrl}tms/produto/listar`, {
          tipo: 'combustivel'
        });

        if (response.data && Array.isArray(response.data)) {
          this.produtos = response.data;
        } else {
          this.produtos = [];
        }
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
        this.produtos = [];
      }
    },

    // Métodos de filtro
    aplicarFiltros() {
      this.options.page = 1;
      localStorage.setItem("filtros_veiculos", JSON.stringify(this.filtros));
    },

    limparFiltros() {
      this.filtros = {
        idfilial: null,
        idveiculo: null,
        idproduto: null
      };
      this.aplicarFiltros();
      this.listarVeiculos();
    },

    // Métodos de ações
    visualizarVeiculo(item) {
      this.veiculoSelecionado = { ...item };
      this.dialogVisualizacao = true;
    },

    editarVeiculo(item) {
      this.$router.push({
        name: 'cadastros-veiculos-novo',
        params: { id: item.idveiculo }
      });
    },

    confirmarExclusao(item) {
      this.veiculoParaExcluir = { ...item };
      this.dialogExclusao = true;
    },

    async excluirVeiculo() {
      if (!this.veiculoParaExcluir) return;

      this.excluindo = true;

      try {
        const payload = {
          ...this.veiculoParaExcluir,
          // odometro: (this.veiculoParaExcluir.odometro || 0).toString(),
          acao: "D",
        };

        await axios.post(`${this.backendUrl}tms/dba/veiculo/inserir`, payload);

        this.mostrarSnackbar('Veículo excluído com sucesso!', 'success');
        this.dialogExclusao = false;
        this.veiculoParaExcluir = null;

        // Recarregar lista após exclusão
        await this.listarVeiculos();
      } catch (error) {
        console.error('Erro ao excluir veículo:', error);
        this.mostrarSnackbar('Erro ao excluir veículo', 'error');
      } finally {
        this.excluindo = false;
      }
    },

    mostrarSnackbar(message, type = 'success') {
      const config = {
        success: { color: 'success', icon: 'mdi-check-circle' },
        error: { color: 'error', icon: 'mdi-alert-circle' },
        warning: { color: 'warning', icon: 'mdi-alert' },
        info: { color: 'info', icon: 'mdi-information' }
      };

      this.snackbar = {
        show: true,
        message,
        color: config[type]?.color || 'success',
        icon: config[type]?.icon || 'mdi-check-circle',
        timeout: 4000
      };
    },

    async verificaFiltros() {
      const json = localStorage.getItem("filtros_veiculos");
      const filtros = JSON.parse(json);
      if (filtros) {
        await this.carregarDados(filtros.idfilial);
        this.filtros = filtros;
      } else {
        this.carregarDados();
      }
    },

    async init() {
      await this.verificaFiltros();
    }
  },

  created() {
    this.init();
  },
};
</script>

<style scoped>
.info-item {
  margin-bottom: 0px;
}

.info-label {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  font-weight: 500;
  margin-bottom: 4px;
}

.info-value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.v-data-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table >>> th {
  background-color: #f5f5f5 !important;
  font-weight: 600 !important;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding: 16px 0 0 0;
}
</style>